import { zodResolver } from "@hookform/resolvers/zod";
import {
  AddressesAddressBookDto,
  CreateAddressBookItem,
  SvcCreateAddressBookItem,
  SvcUpdateAddressBookItem,
  UpdateAddressBookItem,
} from "@springtree/eva-services-core";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import router from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import Button from "~/components/common/button";
import myaccount from "~/components/common/myaccount.module.scss";
import Checkbox from "~/components/form/checkbox";
import DynamicAddressFields, { addressMapSeparated } from "~/components/form/dynamic-address-fields";
import { useAuthContext } from "~/contexts/auth";
import { useConfiguration } from "~/contexts/configuration";
import { useAddressRequirements } from "~/hooks/use-address-requirements";
import { useEvaService } from "~/hooks/use-eva-service";
import { basicAnimationStates } from "~/utils/animation-utils";
import crossCountryUtils from "~/utils/cross-country-utils";

import styles from "./address-list.module.scss";

const emptyAddressSchema = z.object({
  address: z.object({}),
  favouriteBilling: z.boolean().optional(),
  favouriteShipping: z.boolean().optional(),
});

type Props = {
  address?: AddressesAddressBookDto;
  onAfterSubmit?: (addressId?: string) => void;
  noWrapper?: boolean;
  noInnerWrapper?: boolean;
  submitVariant?: "primary" | "secondary";
};

export default function AddressForm(props: Props) {
  const t = useTranslations();
  const { user, isAuthenticated } = useAuthContext();
  const { configuration } = useConfiguration();

  const { requirements, subdivisions, addressSchema, invalidateCache } = useAddressRequirements(
    configuration.OrganizationUnitCulture.CountryID
  );
  const schema = emptyAddressSchema.extend({ address: addressSchema });
  type ValidationSchema = z.infer<typeof schema>;

  const [submitSuccessful, setSubmitSuccessful] = useState(false);
  const currentCountry = crossCountryUtils.getCurrentCountryCode(router);

  // resets submitSuccessful after 5 sec
  useEffect(() => {
    let timeOut: NodeJS.Timeout;
    if (submitSuccessful) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      timeOut = setTimeout(() => {
        setSubmitSuccessful(false);
      }, 5000);
    }
    return () => clearTimeout(timeOut);
  }, [submitSuccessful]);

  const createAddress = useEvaService(SvcCreateAddressBookItem);
  const updateAddressListItem = useEvaService(SvcUpdateAddressBookItem);

  const createAddressHandler = async (data: CreateAddressBookItem) => {
    invalidateCache();
    const createResponse = await createAddress(data);

    setSubmitSuccessful(true);
    const cleanAddressKeys = {} as any;
    Object.entries(data.Address as any).forEach(([key, value]) => {
      if (key != "CountryID" && !addressMapSeparated.includes(key)) {
        cleanAddressKeys[key] = "";
      }
    });
    reset({
      address: {
        ...cleanAddressKeys,
      },
    });
    props.onAfterSubmit?.(createResponse?.ID);
  };

  const updateAddressHandler = async (data: UpdateAddressBookItem) => {
    invalidateCache();
    await updateAddressListItem(data);
    props.onAfterSubmit?.();
  };

  const formMethods = useForm<ValidationSchema>({
    resolver: zodResolver(schema),
    shouldFocusError: false,
    mode: "all",
    defaultValues: {
      address: {
        // Override any info with the existing address
        ...(props.address ? props.address.Address : {}),

        // Static data
        CountryID: currentCountry,
      },
    },
  });

  useEffect(() => {
    // When the user is authenticated, prefill the address form with their data.
    if (user) {
      if (user.FirstName && !formMethods.getValues("address.FirstName")) {
        formMethods.setValue("address.FirstName", user.FirstName);
      }
      if (user.LastName && !formMethods.getValues("address.LastName")) {
        formMethods.setValue("address.LastName", user.LastName);
      }
      if (user.EmailAddress && !formMethods.getValues("address.EmailAddress")) {
        formMethods.setValue("address.EmailAddress", user.EmailAddress);
      }
      if (user.PhoneNumber && !formMethods.getValues("address.PhoneNumber")) {
        formMethods.setValue("address.PhoneNumber", user.PhoneNumber);
      }
    }
  }, [user, formMethods]);

  const { handleSubmit, reset, control, formState, setValue, watch } = formMethods;

  useEffect(() => {
    if (isAuthenticated && user?.EmailAddress) {
      setValue("address.EmailAddress" as any, user?.EmailAddress);
    }
  }, [isAuthenticated, setValue, user?.EmailAddress]);

  const currentAddress1 = watch("address.Address1" as any);

  const onSubmit: SubmitHandler<ValidationSchema> = async (data) => {
    const addressData = {
      Address: {
        ...data.address,
      },
      UseAsDefaultShippingAddress: data.favouriteShipping,
      UseAsDefaultBillingAddress: data.favouriteBilling,
    };

    // new address or update existent
    if (!props.address?.ID) {
      const dataToSubmit = {
        UserID: user?.ID,
        ...addressData,
      };
      createAddressHandler(dataToSubmit);
    } else {
      const dataToSubmit = {
        ID: props.address.ID,
        ...addressData,
      };
      updateAddressHandler(dataToSubmit);
    }
  };

  if (!isAuthenticated) {
    return <></>;
  }

  return (
    <div
      className={classNames(!props.noWrapper ? [myaccount.myaccountCard, styles.addAddressCard] : styles.editAddress)}
    >
      {!props.noWrapper && (
        <div className={myaccount.myaccountCardHeader}>
          <h2>{t("generic.myaccount.addresses.form.title")}</h2>
          <AnimatePresence>
            {submitSuccessful && (
              <motion.p
                className={myaccount.successSubmit}
                initial={basicAnimationStates.off}
                animate={basicAnimationStates.on}
                exit={basicAnimationStates.off}
                aria-live="polite"
              >
                {t("generic.successfully_added")}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      )}
      <div className={!props.noInnerWrapper ? myaccount.myaccountCardContent : ""}>
        <FormProvider {...formMethods}>
          <form
            autoComplete="_off"
            onSubmit={(e) => {
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
            className={styles.formWrapper}
          >
            <DynamicAddressFields
              requirements={requirements}
              subdivisions={subdivisions}
              fieldPrefix="address"
              onAutocompleteSelection={(newAddress) => {
                for (const [key, value] of Object.entries(newAddress)) {
                  if (!!value) {
                    setValue(key as any, value, { shouldValidate: true });
                  }
                }
              }}
              watchedAddress={currentAddress1}
            />
            {/* Do not show checkboxes in the edit form */}
            {!props.address ? (
              <div className={styles.checkboxWrapper}>
                <Controller
                  name="favouriteShipping"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={
                        field.value
                          ? field.value
                          : props.address?.DefaultShippingAddress
                          ? props.address?.DefaultShippingAddress
                          : false
                      }
                      label={t("generic.myaccount.addresses.form.checkbox_shipping")}
                      customStyles={{ container: styles.checkboxContainer }}
                    />
                  )}
                />
                <Controller
                  name="favouriteBilling"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={
                        field.value
                          ? field.value
                          : props.address?.DefaultBillingAddress
                          ? props.address?.DefaultBillingAddress
                          : false
                      }
                      label={t("generic.myaccount.addresses.form.checkbox_billing")}
                      customStyles={{ container: styles.checkboxContainer }}
                    />
                  )}
                />
              </div>
            ) : (
              <br />
            )}
            <Button variant={props.submitVariant ?? "secondary"} type="submit" loading={formState.isSubmitting}>
              {t("generic.myaccount.addresses.form.save_cta")}
            </Button>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
